import React from 'react';
import {
  Box, Button, ButtonText, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import { ArrowLeftRight } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { ScheduledChangeDetail, ScheduledQuestion } from '@/common/api/models';
import { OralExamQuestionText } from '@/common/components/OralExamQuestionText';
import { groupQuestionsByTopic } from '../utils/groupByTopic';
import { questionTitleRepeatsTopic } from '../utils/questionTitleRepeatsTopic';

interface Props {
  change: ScheduledChangeDetail;
  onSwap?: (questionId: string) => void;
}

export const OralQuestionUpdateReview: React.FC<Props> = ({
  change,
  onSwap,
}) => {
  const { t } = useTranslation();
  const groups = groupQuestionsByTopic(change.questions, t('lecturer.scheduledChanges.oralQuestions.otherTopic'));
  const groupedTopicKeys = new Set(groups.map((group) => group.conceptKey));
  const emptyTopics = (change.emptyTopics ?? []).filter(
    (topicName) => !groupedTopicKeys.has(topicName),
  );
  const isScheduled = change.state === 'scheduled';
  const noAlternatives = isScheduled && (change.alternativesLeft ?? 0) <= 0;
  const topicCount = groups.length;

  const renderQuestion = (row: ScheduledQuestion, topicLabel: string) => {
    const qid = row.question?.questionId ?? '';
    return (
      <Box
        key={qid}
        testID={`scheduled-question-${qid}`}
        borderWidth={1}
        borderColor="$borderLight"
        bg="$bgCard"
        borderRadius="$md"
        px="$3"
        py="$2.5"
        alignItems="flex-start"
      >
        <HStack space="md" alignItems="flex-start" w="100%">
          <Box flex={1}>
            <OralExamQuestionText
              size="sm"
              question={row.question?.question ?? ''}
              fillKind={row.question?.fillKind}
              fills={row.question?.fills}
              showFillSlot
              hideTitle={questionTitleRepeatsTopic(row.question?.question, topicLabel)}
            />
          </Box>
          {isScheduled && (
            <Button
              size="xs"
              variant="outline"
              action="secondary"
              isDisabled={noAlternatives}
              onPress={() => onSwap?.(qid)}
              testID={`scheduled-change-swap-${qid}`}
            >
              <Icon as={ArrowLeftRight} size="xs" mr="$1" />
              <ButtonText>
                {noAlternatives
                  ? t('lecturer.scheduledChanges.swap.noneLeft')
                  : t('lecturer.scheduledChanges.swap.button')}
              </ButtonText>
            </Button>
          )}
        </HStack>
      </Box>
    );
  };

  return (
    <VStack space="md">
      <Text size="xs" color="$textHint" testID="scheduled-change-meta">
        {t('lecturer.scheduledChanges.oralQuestions.meta', {
          questions: change.questionCount ?? change.questions.length,
          topics: topicCount,
        })}
        {isScheduled && (
          <>
            {' · '}
            {(change.alternativesLeft ?? 0) > 0
              ? t('lecturer.scheduledChanges.oralQuestions.alternativesLeft', {
                count: change.alternativesLeft,
              })
              : t('lecturer.scheduledChanges.swap.noneLeft')}
          </>
        )}
      </Text>
      {groups.map((group) => (
        <VStack key={group.conceptKey} space="sm">
          <HStack alignItems="baseline" space="sm">
            <Text size="sm" fontFamily="$heading" color="$textHeading">
              {group.label}
            </Text>
            {group.questions.length > 1 && (
              <Text size="xs" color="$textHint">
                {t('lecturer.scheduledChanges.oralQuestions.topicCount', { count: group.questions.length })}
              </Text>
            )}
          </HStack>
          <VStack space="sm">
            {group.questions.map((row) => renderQuestion(row, group.label))}
          </VStack>
        </VStack>
      ))}
      {emptyTopics.map((topicName, index) => (
        <VStack key={`empty-${topicName}`} space="sm" testID={`scheduled-empty-topic-${index}`}>
          <Text size="sm" fontFamily="$heading" color="$textHeading">
            {topicName}
          </Text>
          <Text size="xs" color="$textHint">
            {t('lecturer.scheduledChanges.oralQuestions.emptyTopic')}
          </Text>
        </VStack>
      ))}
    </VStack>
  );
};
